import { useQuery } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import {
  GET_CONVERSATION_MESSAGES_BACKWARD_QUERY,
  GET_CONVERSATION_MESSAGES_QUERY,
  type GetConversationMessagesBackwardQueryVariables,
  type GetConversationMessagesQueryVariables,
} from "../graphql/queries";
import { type Message, type MessageContent } from "../components/chat/types";
import { DEFAULT_PAGE_SIZE } from "../constants/pagination";
import {
  type PaginationError,
  calculateRetryDelay,
  classifyPaginationError,
  shouldRetryError
} from "../utils/paginationErrors";
import {
  type PaginationDirection,
  type PaginationLoadingState
} from "../types/pagination";

interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor?: string;
  endCursor?: string;
}

interface ConversationMessageEdge {
  node: {
    id: string;
    conversationId: string;
    senderRole: "USER" | "AGENT";
    content: Record<string, unknown>;
    timestamp: string;
    metadata?: Record<string, unknown>;
  };
  cursor: string;
}


interface ConversationMessageConnection {
  edges: ConversationMessageEdge[];
  pageInfo: PageInfo;
  totalCount?: number;
}

interface GetConversationMessagesData {
  getConversationMessages: ConversationMessageConnection;
}

interface UseConversationMessagesProps {
  conversationId: string;
  first?: number;
  after?: string;
  last?: number;
  before?: string;
  enabled?: boolean;
  maxRetries?: number;
}

export function useConversationMessages({
  conversationId,
  first = DEFAULT_PAGE_SIZE,
  after,
  last,
  before,
  enabled = true,
  maxRetries = 3,
}: UseConversationMessagesProps) {
  // Retry state management
  const [retryState, setRetryState] = useState({
    retryCount: 0,
    isRetrying: false,
    lastError: null as PaginationError | null,
  });

  // Enhanced loading state management
  const [loadingState, setLoadingState] = useState<PaginationLoadingState>({
    isLoading: false,
    direction: null,
    estimatedMessageCount: 0,
    loadingProgress: 0,
    hasError: false,
  });

  // Function to execute pagination with retry logic and enhanced loading states
  const executeWithRetry = useCallback(async (
    operation: () => Promise<unknown>,
    operationType: 'loadMore' | 'loadPrevious' | 'refetch'
  ): Promise<unknown> => {
    const direction: PaginationDirection = operationType === 'loadMore' ? 'next' : 'previous';
    const estimatedCount = first || last || DEFAULT_PAGE_SIZE;

    const attemptOperation = async (attempt: number): Promise<unknown> => {
      try {
        // Set loading state
        setLoadingState({
          isLoading: true,
          direction: operationType === 'refetch' ? null : direction,
          estimatedMessageCount: estimatedCount,
          loadingProgress: 0,
          hasError: false,
        });

        setRetryState(prev => ({ ...prev, isRetrying: attempt > 0 }));

        // Execute operation with progress simulation
        const progressInterval = setInterval(() => {
          setLoadingState(prev => ({
            ...prev,
            loadingProgress: Math.min(prev.loadingProgress + 20, 90)
          }));
        }, 200);

        const result = await operation();

        // Complete loading
        clearInterval(progressInterval);
        setLoadingState({
          isLoading: false,
          direction: null,
          estimatedMessageCount: 0,
          loadingProgress: 100,
          hasError: false,
        });

        // Reset retry state on success
        setRetryState({ retryCount: 0, isRetrying: false, lastError: null });
        return result;
      } catch (error) {
        const paginationError = classifyPaginationError(error as Error);

        // Update loading state with error
        setLoadingState(prev => ({
          ...prev,
          isLoading: false,
          hasError: true,
          errorMessage: paginationError.message,
        }));

        if (shouldRetryError(paginationError, attempt, maxRetries)) {
          const delay = calculateRetryDelay(attempt);

          setRetryState({
            retryCount: attempt + 1,
            isRetrying: true,
            lastError: paginationError,
          });

          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, delay));
          return attemptOperation(attempt + 1);
        } else {
          // Final failure
          setRetryState({
            retryCount: attempt,
            isRetrying: false,
            lastError: paginationError,
          });
          throw paginationError;
        }
      }
    };

    return attemptOperation(0);
  }, [maxRetries, first, last]);

  // Clear error state
  const clearError = useCallback(() => {
    setRetryState({ retryCount: 0, isRetrying: false, lastError: null });
  }, []);
  // Determine which query and variables to use based on pagination direction
  const isBackwardPagination = last !== undefined || before !== undefined;

  // Memoize query variables to prevent recreation on every render
  const queryVariables = useMemo(() => {
    if (isBackwardPagination) {
      const backwardVariables: GetConversationMessagesBackwardQueryVariables = {
        conversationId,
        ...(last !== undefined && { last }),
        ...(before !== undefined && { before }),
      };
      return backwardVariables;
    } else {
      const forwardVariables: GetConversationMessagesQueryVariables = {
        conversationId,
        ...(first !== undefined && { first }),
        ...(after !== undefined && { after }),
      };
      return forwardVariables;
    }
  }, [conversationId, first, after, last, before, isBackwardPagination]);
  
  // Debug logging for cache keys (development only)
  if (process.env.NODE_ENV === 'development') {
    console.log('[useConversationMessages] Query variables:', {
      conversationId,
      variables: queryVariables,
      isBackwardPagination,
    });
  }

  const { data, loading, error, refetch, fetchMore } = useQuery<GetConversationMessagesData>(
    isBackwardPagination ? GET_CONVERSATION_MESSAGES_BACKWARD_QUERY : GET_CONVERSATION_MESSAGES_QUERY,
    {
      variables: queryVariables,
      skip: !enabled || !conversationId,
      fetchPolicy: "cache-first", // Use cache-first to avoid unnecessary network requests
      errorPolicy: "all",
    }
  );

  // Helper function to ensure content is properly typed
  const parseMessageContent = (content: unknown): MessageContent => {
    // Content should already be an object from the backend
    if (typeof content === 'object' && content !== null) {
      return content as MessageContent;
    }
    // Fallback for unexpected content types
    console.warn('Unexpected content type:', typeof content, content);
    return { text: String(content) };
  };

  // Transform GraphQL messages to frontend Message type (memoized for performance)
  // This prevents O(n) operations on every render - only runs when Apollo data changes
  const messages: Message[] = useMemo(() => {
    if (!data?.getConversationMessages?.edges) return [];
    
    return data.getConversationMessages.edges.map(edge => {
      // Only log in development builds to reduce production overhead
      if (process.env.NODE_ENV === 'development') {
        console.log('[useConversationMessages] Processing message:', {
          id: edge.node.id,
          content: edge.node.content,
          contentType: typeof edge.node.content,
        });
      }
      
      return {
        id: edge.node.id,
        conversationId: edge.node.conversationId,
        senderRole: edge.node.senderRole,
        content: parseMessageContent(edge.node.content),
        timestamp: edge.node.timestamp,
        metadata: edge.node.metadata || {},
      };
    });
  }, [data?.getConversationMessages?.edges]);

  const pageInfo = data?.getConversationMessages?.pageInfo;
  const totalCount = data?.getConversationMessages?.totalCount;

  // Helper function to validate cursor format
  const validateCursor = useCallback((cursor: string, direction: 'before' | 'after') => {
    if (!cursor) return true; // Empty cursor is valid
    
    // Check for obvious corruption patterns
    if (cursor.includes('_')) {
      console.error(`[useConversationMessages] Invalid cursor detected (${direction}): contains underscore`, cursor);
      return false;
    }
    
    if (cursor.includes(' ')) {
      console.error(`[useConversationMessages] Invalid cursor detected (${direction}): contains spaces`, cursor);
      return false;
    }
    
    // Check if it looks like valid base64
    try {
      atob(cursor);
      if (process.env.NODE_ENV === 'development') {
        console.log(`[useConversationMessages] Valid cursor (${direction}):`, cursor.substring(0, 50) + (cursor.length > 50 ? '...' : ''));
      }
      return true;
    } catch (error) {
      console.error(`[useConversationMessages] Invalid cursor detected (${direction}): not valid base64`, cursor, error);
      return false;
    }
  }, []);

  // Function to load more messages (forward pagination) with retry
  const loadMore = useCallback(async () => {
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor) return;

    // Validate cursor before sending request
    if (!validateCursor(pageInfo.endCursor, 'after')) {
      console.error('[useConversationMessages] Skipping loadMore due to invalid cursor:', pageInfo.endCursor);
      return;
    }

    return executeWithRetry(async () => {
      return fetchMore({
        query: GET_CONVERSATION_MESSAGES_QUERY,
        variables: {
          conversationId,
          first,
          after: pageInfo.endCursor,
        },
        updateQuery: (prev, { fetchMoreResult }): GetConversationMessagesData => {
          if (!fetchMoreResult) return prev;

          // Convert GraphQL edges to our interface type
          const convertEdge = (edge: { node: { id: string; conversationId: string; senderRole: string; content: Record<string, unknown>; timestamp: string; metadata?: Record<string, unknown> | null }; cursor: string }): ConversationMessageEdge => ({
            node: {
              id: edge.node.id,
              conversationId: edge.node.conversationId,
              senderRole: edge.node.senderRole as "USER" | "AGENT",
              content: parseMessageContent(edge.node.content),
              timestamp: edge.node.timestamp,
              metadata: edge.node.metadata || undefined,
            },
            cursor: edge.cursor,
          });

          return {
            getConversationMessages: {
              ...prev.getConversationMessages,
              edges: [
                ...prev.getConversationMessages.edges,
                ...fetchMoreResult.getConversationMessages.edges.map(convertEdge),
              ],
              pageInfo: {
                ...prev.getConversationMessages.pageInfo,
                endCursor: fetchMoreResult.getConversationMessages.pageInfo.endCursor || undefined,
                hasNextPage: fetchMoreResult.getConversationMessages.pageInfo.hasNextPage,
              },
              totalCount:
                fetchMoreResult.getConversationMessages.totalCount ??
                prev.getConversationMessages.totalCount,
            },
          };
        },
      });
    }, 'loadMore');
  }, [pageInfo?.hasNextPage, pageInfo?.endCursor, executeWithRetry, fetchMore, conversationId, first, validateCursor]);

  // Function to load previous messages (backward pagination) with retry
  const loadPrevious = useCallback(async () => {
    if (!pageInfo?.hasPreviousPage || !pageInfo.startCursor) return;

    // Validate cursor before sending request
    if (!validateCursor(pageInfo.startCursor, 'before')) {
      console.error('[useConversationMessages] Skipping loadPrevious due to invalid cursor:', pageInfo.startCursor);
      return;
    }

    return executeWithRetry(async () => {
      return fetchMore({
        query: GET_CONVERSATION_MESSAGES_BACKWARD_QUERY,
        variables: {
          conversationId,
          last: first, // Use same limit for consistency
          before: pageInfo.startCursor,
        },
        updateQuery: (prev, { fetchMoreResult }): GetConversationMessagesData => {
          if (!fetchMoreResult) return prev;

          // Convert GraphQL edges to our interface type
          const convertEdge = (edge: { node: { id: string; conversationId: string; senderRole: string; content: Record<string, unknown>; timestamp: string; metadata?: Record<string, unknown> | null }; cursor: string }): ConversationMessageEdge => ({
            node: {
              id: edge.node.id,
              conversationId: edge.node.conversationId,
              senderRole: edge.node.senderRole as "USER" | "AGENT",
              content: parseMessageContent(edge.node.content),
              timestamp: edge.node.timestamp,
              metadata: edge.node.metadata || undefined,
            },
            cursor: edge.cursor,
          });

          return {
            getConversationMessages: {
              ...prev.getConversationMessages,
              edges: [
                ...fetchMoreResult.getConversationMessages.edges.map(convertEdge),
                ...prev.getConversationMessages.edges,
              ],
              pageInfo: {
                ...prev.getConversationMessages.pageInfo,
                startCursor: fetchMoreResult.getConversationMessages.pageInfo.startCursor || undefined,
                hasPreviousPage: fetchMoreResult.getConversationMessages.pageInfo.hasPreviousPage,
              },
              totalCount:
                fetchMoreResult.getConversationMessages.totalCount ??
                prev.getConversationMessages.totalCount,
            },
          };
        },
      });
    }, 'loadPrevious');
  }, [pageInfo?.hasPreviousPage, pageInfo?.startCursor, executeWithRetry, fetchMore, conversationId, first, validateCursor]);

  // Enhanced refetch with retry
  const refetchWithRetry = useCallback(async () => {
    return executeWithRetry(async () => {
      return refetch();
    }, 'refetch');
  }, [executeWithRetry, refetch]);

  return {
    messages,
    loading,
    error,
    refetch: refetchWithRetry,
    pageInfo,
    totalCount,
    loadMore,
    loadPrevious,
    hasNextPage: pageInfo?.hasNextPage || false,
    hasPreviousPage: pageInfo?.hasPreviousPage || false,
    // Error handling features
    paginationError: retryState.lastError,
    isRetrying: retryState.isRetrying,
    retryCount: retryState.retryCount,
    clearError,
    maxRetries,
    // Enhanced loading state features
    loadingState,
    isLoadingPrevious: loadingState.isLoading && loadingState.direction === 'previous',
    isLoadingMore: loadingState.isLoading && loadingState.direction === 'next',
    loadingProgress: loadingState.loadingProgress,
    estimatedMessageCount: loadingState.estimatedMessageCount,
  };
}
